import { useRef } from "react";
import { useIbkrData, useVisible } from "./poller";
import { PositionRow } from "./types";
import { C, IconButton, IntroHeader, SideTag, SymbolCell, WidgetBox, fmt, freshnessDot, useNarrow } from "./ui";
import { WidgetProps, openLadder, openLimit, referencePrice } from "./windows";

const GRID = "1.9fr .8fr .9fr .9fr 1.3fr 78px";

function Pnl({ value }: { value: number | null }) {
  if (value == null) return <span style={{ textAlign: "right", fontSize: 13 }}>—</span>;
  const up = value >= 0;
  return (
    <span style={{ textAlign: "right", fontSize: 13, fontWeight: 600, color: up ? C.gain : C.loss, whiteSpace: "nowrap" }}>
      {up ? "▲ +" : "▼ −"}{Math.abs(value).toFixed(2)}
    </span>
  );
}

// positions with live last price and unrealized P&L. Ladder opens a Buy / Sell
// window; reverse opens a limit order against the position, same size.
export function PositionsWidget({ widget, actions, intro }: WidgetProps) {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useVisible(ref);
  const narrow = useNarrow(ref);
  const { data, freshness, error } = useIbkrData<PositionRow[]>("positions", {}, visible);
  const positions = data ?? [];
  const total = positions.reduce((sum, p) => sum + (p.unrealizedPnl ?? 0), 0);

  const reverse = (p: PositionRow) => {
    const sell = p.size > 0;
    const size = Math.abs(p.size);
    const sym = p.contract.label;
    openLimit(actions, p.contract, referencePrice(p), sell ? "SELL" : "BUY", size,
      `Reverse ${sym}: ${sell ? "sell" : "buy"} ${size} against your ${sell ? "long" : "short"} ${size} position. Press Submit to send it.`);
  };

  return (
    <>
      <IntroHeader stampLine={intro} text={`${positions.length} open positions.`} />
      <WidgetBox boxRef={ref} headerAlign="center" title={<span style={{ fontWeight: 600 }}>Positions ({positions.length})</span>} status={freshnessDot(freshness)} onDuplicate={() => actions.duplicate({ ...widget.params })} onClose={actions.close}>
        {!narrow && <div style={{ display: "grid", gridTemplateColumns: GRID, gap: 8, alignItems: "center", padding: "6px 10px", color: C.muted, borderBottom: `1px solid ${C.divider}` }}>
          <span>Symbol</span><span style={{ textAlign: "right" }}>Qty</span><span style={{ textAlign: "right" }}>Avg</span>
          <span style={{ textAlign: "right" }}>Last</span><span style={{ textAlign: "right" }}>P&amp;L</span><span></span>
        </div>}
        {positions.length === 0 && <div style={{ padding: "8px 10px", color: C.muted }}>{error ?? (data ? "No positions." : "Loading…")}</div>}
        {positions.map((p, i) => {
          const tag = <SideTag side={p.size > 0 ? "BUY" : "SELL"} label={p.size > 0 ? "LONG" : "SHORT"} />;
          const qty = p.size > 0 ? String(p.size) : `−${Math.abs(p.size)}`;
          const buttons = (
            <span style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
              <IconButton icon="ladder" label={`Ladder buy / sell for ${p.contract.label}`} title="Ladder buy / sell"
                onClick={() => openLadder(actions, p.contract, Math.abs(p.size))} />
              <IconButton icon="reverse" label={`Reverse ${p.contract.label} position`} title="Reverse position" onClick={() => reverse(p)} />
            </span>
          );
          const border = i > 0 ? `1px solid ${C.divider}` : undefined;
          // phones: symbol over "qty @ avg · last", P&L and buttons on the right
          if (narrow) {
            return (
              <div key={p.contract.conId} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderTop: border }}>
                <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                  <SymbolCell tag={tag} contract={p.contract} inline />
                  <span style={{ color: C.muted }}>{qty} @ {fmt(p.avgPrice)} · last {fmt(p.mark)}</span>
                </span>
                <Pnl value={p.unrealizedPnl} />
                {buttons}
              </div>
            );
          }
          return (
            <div key={p.contract.conId} style={{ display: "grid", gridTemplateColumns: GRID, gap: 8, alignItems: "center", padding: "8px 10px", borderTop: border }}>
              <SymbolCell tag={tag} contract={p.contract} />
              <span style={{ textAlign: "right", fontSize: 13 }}>{qty}</span>
              <span style={{ textAlign: "right", fontSize: 13 }}>{fmt(p.avgPrice)}</span>
              <span style={{ textAlign: "right", fontSize: 13 }}>{fmt(p.mark)}</span>
              <Pnl value={p.unrealizedPnl} />
              {buttons}
            </div>
          );
        })}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", borderTop: `1px solid ${C.border}`, background: C.headerBg }}>
          <span style={{ color: C.muted }}>Unrealized total:</span>
          <Pnl value={positions.length ? total : null} />
        </div>
      </WidgetBox>
    </>
  );
}
